import {
  ICatalog,
  ICatalogData,
  ISaveCatalogPayload,
  ICatalogTemplates,
} from "Interfaces/interface.catalog";
import ecomInterceptor from "../interceptors/ecomInterceptor";
import { IProduct } from "Interfaces/interface.productsapi";
import { extractErrorMessage } from "utils/ErrorHandlers";
import toast from "react-hot-toast";

export interface GeneratePdfResponse {
  catalogHtml: {
    success: boolean;
    isChunked?: boolean;
    html?: string; // Old format
    frontHtml?: string;
    productChunks?: string[];
    lastHtml?: string;
    totalProducts?: number;
    chunksCount?: number;
    message?: string;
  };
  deviceFingerprint: string;
}

const catalogService = {
  async fetchTemplates(): Promise<ICatalogTemplates[]> {
    const response = await ecomInterceptor.get("/catalog/templates");
    return response.data.data;
  },

  async fetchProducts(): Promise<IProduct[]> {
    const response = await ecomInterceptor.get("/catalog/products");
    return response.data;
  },

  async generatePDF(data: ICatalogData): Promise<GeneratePdfResponse> {
    try {
      const response = await ecomInterceptor.post<GeneratePdfResponse>("/catalog/generate", data);
      return response.data;
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message || "Failed to generate catalog HTML.");
      throw error;
    }
  },

  /** Upload generated PDF Blob to BE webhook */
  async uploadGeneratedCatalog(payload: {
    catalogName: string;
    deviceFingerprint: string;
    catalogBlob: Blob;
  }): Promise<void> {
    const { catalogName, deviceFingerprint, catalogBlob } = payload;

    const form = new FormData();
    form.append("catalogName", catalogName);
    form.append("deviceFingerprint", deviceFingerprint);
    form.append("catalog", catalogBlob, "catalog.pdf");

    try {
      await ecomInterceptor.post("/webhook/save-catalog", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Catalog downloaded and saved to profile successfully!");
    } catch (error: any) {
      console.error("Upload catalog error:", error);
      const msg = extractErrorMessage(error);
      toast.error(msg || "Failed to save catalog.");
      throw error;
    }
  },

  async saveCatalog(data: ISaveCatalogPayload): Promise<void> {
    const formData = new FormData();
    const blob = new Blob([data.catalog], { type: "application/pdf" });

    formData.append("userId", data.userId);
    formData.append("catalog", blob, "catalog.pdf");
    formData.append("catalogName", data.catalogName);

    try {
      await ecomInterceptor.post("/catalog/save", formData);
    } catch (error: any) {
      console.error(
        "Catalog save error:",
        error?.response?.data || error.message
      );
      throw new Error("Failed to save catalog.");
    }
  },

  // catalogService.ts
  async downloadCatalog(catalogId: string): Promise<string> {
    try {
      const response = await ecomInterceptor.get(
        `/catalog/download/${catalogId}`
      );
      return response.data;
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message || "Failed to download catalog.");
      throw error;
    }
  },

  async userCatalogs(): Promise<ICatalog[]> {
    try {
      const response = await ecomInterceptor.get("/users/get-user-catalogs");
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(
        "Failed to fetch Catalogs:",
        error?.response?.data || error.message
      );
      throw new Error("Failed to fetch Catalogs");
    }
  },

  async deleteCatalog(
    catalogId: string
  ): Promise<{ updatedCatalogs: ICatalog[] }> {
    try {
      const response = await ecomInterceptor.delete("/users/delete-catalog", {
        data: { catalogId },
      });
      return response.data;
    } catch (error: any) {
      console.error(
        "Failed to delete catalog:",
        error?.response?.data || error.message
      );
      throw new Error("Failed to delete catalog");
    }
  },
};

export default catalogService;
